/**
 * Global MJPEG stream concurrency limiter.
 *
 * ROOT CAUSE this guards against:
 * Every camera is rendered as an MJPEG `<img>` (see VideoUrl). An MJPEG image
 * holds a persistent HTTP connection open for the whole life of the stream.
 * In production the stream URLs are relative (`/getVideo/http://...mjpg`, see
 * services/prepareVideoUrls when `config.localver` is false), so ALL streams are
 * proxied through a single host. Browsers allow only ~6 concurrent persistent
 * connections per host over HTTP/1.1, so when the operator opens several rack
 * windows and the total number of live streams exceeds 6, the 7th+ connection
 * stalls forever and the video stays black.
 *
 * A full fix requires a transport/backend change (HTTP/2 multiplexing, a
 * WebSocket/WebRTC video channel, or domain-sharding the stream host). Until
 * then this limiter is the client-side mitigation: it caps the number of live
 * MJPEG connections and keeps the most-recently-requested (newest / focused)
 * streams active, deactivating the rest so they release their connection slot.
 * Closing or unfocusing a window frees a slot for a queued stream.
 */

const DEFAULT_MAX = 6;

/**
 * Max concurrent live streams. Configurable via window.config.max_video_streams,
 * defaults to 6 (the typical per-host HTTP/1.1 connection limit).
 */
export const getStreamMax = () => {
  const v = Number(window?.config?.max_video_streams);
  return Number.isFinite(v) && v > 0 ? v : DEFAULT_MAX;
};

class StreamLimiter {
  constructor() {
    // key -> { seq, active, activate, deactivate }
    this.entries = new Map();
    this.seq = 0;
  }

  /**
   * Register a stream. `activate` is called when it may hold a live connection,
   * `deactivate` when it must release it. Newly registered streams get the
   * highest priority (newest wins).
   */
  register(key, activate, deactivate) {
    this.entries.set(key, { seq: ++this.seq, active: false, activate, deactivate });
    this._reconcile();
  }

  /**
   * Bump a stream to the front of the queue (e.g. on user interaction / focus)
   * so a currently-queued stream can preempt an older one.
   */
  touch(key) {
    const e = this.entries.get(key);
    if (e) {
      e.seq = ++this.seq;
      this._reconcile();
    }
  }

  /**
   * Remove a stream (on unmount / window close) and free its slot.
   */
  release(key) {
    if (this.entries.delete(key)) {
      this._reconcile();
    }
  }

  /**
   * Keep the newest `max` streams active, deactivate the rest.
   */
  _reconcile() {
    const max = getStreamMax();
    const arr = [...this.entries.values()].sort((a, b) => b.seq - a.seq);
    arr.forEach((e, i) => {
      const shouldActive = i < max;
      if (shouldActive && !e.active) {
        e.active = true;
        try {
          e.activate();
        } catch (err) {
          /* noop */
        }
      } else if (!shouldActive && e.active) {
        e.active = false;
        try {
          e.deactivate();
        } catch (err) {
          /* noop */
        }
      }
    });
  }
}

export const streamLimiter = new StreamLimiter();
